/**
 * Market Stats
 *
 * A simplified Contentful component that displays market-level statistics.
 * Uses the marketIdentifier from CMS to fetch and display market metrics.
 */
import { Box, Col } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { globalObservability } from '@opendoor/observability/slim';
import { LandingPageSectionContainer } from 'components/landing-pages-v2/shared/Container';
import type { Entry } from 'contentful';
import type { ILpComponentMarketStatsFields } from 'declarations/contentful';
import type { MarketMetrics } from 'helpers/marketMetrics';
import { getMarketMetrics } from 'helpers/marketMetrics';

const Sentry = globalObservability.getSentryClient();

/**
 * Data loader function - called server-side during getServerSideProps
 * This fetches the market metrics data from Athena before rendering
 */
export const loader = async (
  entry: Entry<ILpComponentMarketStatsFields>,
): Promise<MarketMetrics | null> => {
  const marketIdentifier = entry.fields.marketIdentifier;

  if (!marketIdentifier) {
    return null;
  }

  try {
    const metrics = await getMarketMetrics(marketIdentifier);
    return metrics;
  } catch (error) {
    Sentry?.captureException?.(error, {
      captureContext: {
        extra: {
          marketIdentifier,
          entryId: entry.sys.id,
        },
      },
    });
    return null;
  }
};

const formatChange = (changeValue: number | null | undefined): string => {
  if (changeValue === null || changeValue === undefined) return '';
  const arrow = changeValue < 0 ? '↘' : '↗';
  const percentage = Math.round(Math.abs(changeValue) * 100);
  return `${arrow} ${percentage}%`;
};

/**
 * Individual stat card component
 */
const StatCard: React.FC<{
  value: string | number;
  change?: number | null;
  label: string;
  sublabel?: string;
  reverseTrendColor?: boolean;
  fullWidth?: boolean;
}> = ({ value, change, label, sublabel, reverseTrendColor = false, fullWidth = false }) => {
  const getTrendColorClass = (changeValue: number | null | undefined): string => {
    if (changeValue === null || changeValue === undefined) {
      return 'text-od-content-secondary';
    }
    if (reverseTrendColor) {
      return changeValue > 0 ? 'text-od-content-error' : 'text-od-content-accent-brand';
    }
    return changeValue < 0 ? 'text-od-content-error' : 'text-od-content-accent-brand';
  };

  return (
    <Col
      className={cn(
        'relative gap-2 rounded-2xl bg-od-bg-primary p-5',
        fullWidth && 'md:col-span-2',
      )}
    >
      {change !== undefined && change !== null && (
        <Text
          as="p"
          className={cn('absolute right-5 top-5 text-body-sm', getTrendColorClass(change))}
        >
          {formatChange(change)}
        </Text>
      )}
      <Text as="h4" className="block text-header-sm text-od-content-primary">
        {typeof value === 'number' ? value.toLocaleString('en-US') : value}
      </Text>
      <Text as="p" className="block text-body-sm text-od-content-primary">
        {label}
        {sublabel && (
          <>
            <br />
            {sublabel}
          </>
        )}
      </Text>
    </Col>
  );
};

/**
 * Main render function - renders the market stats
 *
 * @param entry - The Contentful entry for this component
 * @param metrics - Pre-fetched market metrics data from the loader
 */
const RenderMarketStats = (
  entry: Entry<ILpComponentMarketStatsFields>,
  metrics: MarketMetrics | null | undefined,
) => {
  if (!metrics) {
    return null;
  }

  const fields = entry.fields;

  return (
    <Box className="w-full bg-od-bg-secondary">
      <LandingPageSectionContainer>
        <Col className="gap-6 py-10">
          {/* Header Section */}
          <Col className="md:flex-row md:justify-between">
            <Col className="items-start gap-3 md:w-3/5">
              {fields.title && (
                <Text as="h2" className="block text-subheader-md text-od-content-primary">
                  {fields.title}
                </Text>
              )}
              {fields.subtitle && (
                <Text as="p" className="block text-body-md text-od-content-secondary">
                  {fields.subtitle}
                </Text>
              )}
            </Col>
          </Col>

          {/* Stats Grid */}
          <Col className="gap-2">
            {/* Row 1: New Listings + Homes on Market (50/50 width) */}
            <Grid className="grid-cols-1 gap-3 md:grid-cols-2 md:gap-1">
              {metrics.newListings !== undefined && metrics.newListings !== null && (
                <StatCard
                  value={metrics.newListings}
                  change={metrics.newListingsWow}
                  label="New listings"
                  sublabel="This week"
                />
              )}

              {metrics.homesOnMarket !== undefined && metrics.homesOnMarket !== null && (
                <StatCard
                  value={metrics.homesOnMarket}
                  change={metrics.homesOnMarketWow}
                  label="Homes on market"
                  sublabel="Currently active"
                />
              )}
            </Grid>

            {/* Row 2: Homes Delisted + Homes Sold (50/50 width) */}
            <Grid className="grid-cols-1 gap-3 md:grid-cols-2 md:gap-1">
              {metrics.delistings !== undefined && metrics.delistings !== null && (
                <StatCard
                  value={metrics.delistings}
                  change={metrics.delistingsWow}
                  label="Homes delisted"
                  sublabel="This week"
                />
              )}

              {metrics.homesSold !== undefined && metrics.homesSold !== null && (
                <StatCard
                  value={metrics.homesSold}
                  change={metrics.homesSoldWow}
                  label="Homes sold"
                  sublabel="This week"
                />
              )}
            </Grid>
          </Col>

          {/* Timestamp */}
          {metrics.timePeriod && (
            <Text
              as="p"
              className="block w-full text-center text-label-sm text-od-content-secondary"
            >
              Last updated on{' '}
              {new Date(metrics.timePeriod).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </Text>
          )}
        </Col>
      </LandingPageSectionContainer>
    </Box>
  );
};

const MarketStats = {
  render: RenderMarketStats,
  loader: loader,
};

export default MarketStats;
